// Mock data for First Page Consultants

export const siteData = {
  hero: {
    headline: "Built by Amazon operators. Run like owners, not VAs.",
    subheadline: "Senior led Amazon Ads and account management for US and Canada based brands that want accountability, structure, and profitable scale.",
    trustLine: "No junior handoffs. No task based execution. Clear ownership from day one.",
    credibility: [
      "10+ years operating inside Amazon",
      "Multiple seven figure brands managed",
      "Active across US, Canada, UK and India marketplaces",
      "Over $500k in Monthly Ad Spend Management",
      "Manages PPC strategy for top 5% Amazon Brands"
    ],
    heroImage: "https://images.pexels.com/photos/2977565/pexels-photo-2977565.jpeg"
  },
  problem: {
    headline: "Amazon growth breaks when ownership is missing",
    painPoints: [
      "Ad spend keeps increasing but margins do not improve",
      "Campaigns grow messy and hard to control",
      "Listings are not built to support advertising efficiency",
      "Reports show numbers but no clear direction",
      "Most agencies operate like task teams, not operators"
    ],
    closeLine: "If Amazon feels unpredictable, the issue is not spend. It is structure and ownership."
  },
  solution: {
    headline: "A disciplined Amazon operating system built for scale",
    pillars: [
      "Clean account and listing foundation before scaling",
      "Demand driven keyword and campaign structure",
      "Controlled launch and intentional expansion",
      "Weekly optimization with real accountability",
      "Reporting that drives decisions, not confusion"
    ],
    closeLine: "We do not chase shortcuts. We build systems that compound."
  },
  services: [
    {
      title: "Amazon Account Management",
      description: "End to end support from Seller Central setup guidance and Brand Registry to launch readiness, account health, and ongoing optimization."
    },
    {
      title: "Amazon Ads Management",
      description: "Hands on management of Sponsored Products, Sponsored Brands, and Sponsored Display with daily optimization and disciplined budget control."
    },
    {
      title: "Amazon DSP and AMC",
      description: "Audience driven strategies using Amazon DSP and AMC insights to unlock incremental growth beyond search."
    },
    {
      title: "Listing Optimization and CRO",
      description: "Keyword driven content designed to convert traffic into sales and improve ad efficiency."
    },
    {
      title: "Audits and Growth Roadmaps",
      description: "Clear diagnostics and execution ready plans built for brands serious about scaling."
    }
  ],
  process: [
    {
      step: 1,
      title: "Audit and Diagnosis",
      description: "We identify where spend leaks and where growth is blocked."
    },
    {
      step: 2,
      title: "Build the Foundation",
      description: "Listings, keywords, and campaigns are rebuilt for clarity and control."
    },
    {
      step: 3,
      title: "Launch and Stabilize",
      description: "Ads go live with tight budgets, bid discipline, and active monitoring."
    },
    {
      step: 4,
      title: "Scale Intentionally",
      description: "We expand only when efficiency and conversion are proven."
    },
    {
      step: 5,
      title: "Optimize and Report",
      description: "Weekly optimization and monthly direction with clear next steps."
    }
  ],
  tools: {
    headline: "Powered by data. Driven by operators.",
    intro: "We use best in class Amazon tools to support smarter decisions, never to replace human judgment.",
    toolsList: [
      {
        name: "Helium 10",
        description: "Keyword research, listing diagnostics, and competitive intelligence."
      },
      {
        name: "Data Dive",
        description: "Demand analysis and keyword prioritization aligned to real market opportunity."
      },
      {
        name: "Adbrew",
        description: "Rule based optimization, bid control, and dayparting to protect margins while scaling."
      }
    ],
    supportLine: "Tools execute faster. Experience decides better."
  },
  why: {
    headline: "Why serious brands choose us",
    differentiators: [
      "Amazon only focus",
      "Operator led strategy with hands on execution",
      "No junior handoffs or task based delivery",
      "Clear communication and disciplined reporting",
      "Built by former Amazon sellers and operators"
    ],
    closeLine: "We work with fewer brands so we can do the work properly."
  },
  proof: [
    "10+ years in Amazon Ads and operations",
    "Brands scaled from zero to multi million revenue",
    "Experience across US, Canada, UK and India",
    "Trusted by DTC brands, aggregators, and VC backed portfolios"
  ],
  qualification: {
    forYou: [
      "Amazon is a serious revenue channel",
      "You want clarity, ownership, and margin discipline",
      "You value operators over executors"
    ],
    notForYou: [
      "You want cheap management",
      "You want hands off automation with no strategy",
      "You are not ready to invest in growth"
    ]
  },
  founder: {
    name: "Hitesh Aswani",
    title: "Founder and Principal",
    bio: "Started as an Amazon seller in 2013. Built, scaled, and exited brands. Now leads a focused Amazon Ads and account management team supporting growth focused brands globally.",
    image: "/hitesh-aswani.jpg"
  },
  finalCTA: {
    headline: "If Amazon is a serious revenue channel, this is the right conversation",
    supportLine: "We partner with a limited number of US focused brands to ensure focus and results.",
    microCopy: "No obligation. Clear direction on the first call."
  },
  footer: {
    companyName: "First Page Consultants",
    description: "Senior led Amazon Ads and account management for serious brands.",
    location: "Mumbai, India"
  },
  testimonials: [
    {
      quote: "Hitesh and his team rebuilt our Amazon presence from the ground up. We went from chasing ACOS every week to actually scaling with confidence. No task-based busywork — just clear ownership and real decisions.",
      name: "Allan Wendling",
      title: "Founder",
      brand: "iPrimio",
      logo: "/brands/iprimio.png"
    },
    {
      quote: "What stood out was the discipline. They fixed our listings and account health first, then scaled ads in a way that actually protected margin. The reporting is the cleanest we've ever worked with.",
      name: "Brand Team",
      title: "E-commerce Lead",
      brand: "Touch Skin Care",
      logo: "/brands/touch.webp"
    },
    {
      quote: "Every other agency wanted to throw more spend at the problem. First Page Consultants went deep on structure — keywords, campaigns, content — before scaling. That patience is exactly why it worked.",
      name: "Operations Team",
      title: "Marketplace Lead",
      brand: "SlipToGrip",
      logo: "/brands/sliptogrip.jpg"
    }
  ],
  faqs: [
    {
      q: "What does First Page Consultants actually do?",
      a: "We are a senior-led Amazon Ads and account management agency. Day to day: Amazon PPC across Sponsored Products / Brands / Display, Seller Central account management, listing optimization, and Amazon DSP / AMC when it makes sense for the brand."
    },
    {
      q: "How much does it cost to work with you?",
      a: "PPC Management is a fixed $799/month retainer. Account & Listing Optimization is $500/month. Both are clean retainers — no upsells, no percentage-of-ad-spend games, no hidden fees. Both plans include direct access to senior operators, not junior handoffs."
    },
    {
      q: "Who is First Page Consultants for?",
      a: "Growth-focused Amazon brands where Amazon is a serious revenue channel — DTC brands, aggregators, and VC-backed portfolios. Best fit: brands doing at least $50k/month in Amazon revenue that want operator-led strategy over task-based execution."
    },
    {
      q: "How long before I see results?",
      a: "Weeks 1–2 are foundation work — audit, negative keywords, campaign hygiene, bid recalibration. Meaningful ACOS and TACOS improvement typically shows in weeks 3–6. Scale phase usually kicks in from week 8 onwards."
    },
    {
      q: "Do you work with US and Canadian brands?",
      a: "Yes. US and Canada are our two core markets. We also work with select UK and India based brands. We run Amazon Ads across amazon.com, amazon.ca, amazon.co.uk, and amazon.in."
    },
    {
      q: "What is your first strategy call like?",
      a: "A 30–45 minute call over Zoom. Directional audit of your current account, discussion of what you want to achieve, and a straight answer on whether we are the right fit. No obligation, and you leave with clear next steps regardless of whether we work together."
    }
  ]
};